import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  GlassCard,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_PRIMARY } from '@/shared/ui-class';
import {
  SEED_INSTALLED_CONNECTORS,
  type InstalledConnectorSeed,
} from '@/versions/v1/shared/settings-seed';
import { formatRelativeTime } from './format-utils';

const TOOL_ACCESS = ['search_issues', 'create_issue', 'read_page', 'update_page'];

export function ConnectorDetailScene() {
  const { connectorId } = useParams<{ connectorId: string }>();
  const navigate = useNavigate();
  const [connector, setConnector] = useState<InstalledConnectorSeed | null>(() =>
    SEED_INSTALLED_CONNECTORS.find((entry) => entry.id === connectorId) ?? null,
  );
  const [enabledTools, setEnabledTools] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(TOOL_ACCESS.map((tool) => [tool, true])),
  );

  const title = connector?.name ?? 'Connector';

  const statusLabel = useMemo(() => {
    if (!connector) return 'Unknown';
    if (connector.needsReauth) return 'Needs re-auth';
    return connector.mcpStatus;
  }, [connector]);

  if (!connector) {
    return (
      <SurfacePageLayout width="narrow">
        <SurfacePageBody className="space-y-4">
          <SettingsPageHeader title="Connector not found" />
          <Link to="/settings/connectors" className="text-sm text-accent-600">
            Back to connectors
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="ConnectorDetailScene">
        <SurfacePageBody className="space-y-6">
          <div>
            <Link
              to="/settings/connectors"
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Connectors
            </Link>
            <SettingsPageHeader title={title} description={connector.description} />
          </div>

          <GlassCard className="space-y-4 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <Pill>{statusLabel}</Pill>
              <Pill>{connector.enabled ? 'Enabled' : 'Disabled'}</Pill>
              <span className="text-xs text-muted">
                Installed {formatRelativeTime(connector.installedAt)}
              </span>
            </div>
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium">Connector enabled</span>
              <input
                type="checkbox"
                checked={connector.enabled}
                onChange={(event) =>
                  setConnector((prev) =>
                    prev ? { ...prev, enabled: event.target.checked } : prev,
                  )
                }
              />
            </label>
          </GlassCard>

          <GlassCard className="overflow-hidden">
            <div className="border-b border-[var(--glass-border)] px-5 py-4">
              <h2 className="text-base font-medium">Tool access</h2>
              <p className="mt-1 text-sm text-muted">
                Choose which tools this connector may invoke in chat.
              </p>
            </div>
            <ul className="divide-y divide-[var(--glass-border)]">
              {TOOL_ACCESS.map((tool) => (
                <li key={tool} className="flex items-center justify-between px-5 py-3">
                  <span className="font-mono text-sm">{tool}</span>
                  <input
                    type="checkbox"
                    checked={enabledTools[tool] ?? false}
                    onChange={(event) =>
                      setEnabledTools((prev) => ({
                        ...prev,
                        [tool]: event.target.checked,
                      }))
                    }
                  />
                </li>
              ))}
            </ul>
          </GlassCard>

          {connector.needsReauth ? (
            <GlassCard className="space-y-3 p-5">
              <h2 className="text-base font-medium">OAuth</h2>
              <p className="text-sm text-muted">
                This connector needs you to sign in again before tools can run.
              </p>
              <button type="button" className={BUTTON_PRIMARY}>
                Reconnect
              </button>
            </GlassCard>
          ) : null}

          <GlassCard className="space-y-3 border-red-200 p-5 dark:border-red-900/50">
            <h2 className="text-base font-medium text-red-700 dark:text-red-300">
              Danger zone
            </h2>
            <p className="text-sm text-muted">
              Uninstall removes this connector and revokes its OAuth grant.
            </p>
            <button
              type="button"
              className={cn(
                'rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-900/60 dark:text-red-400',
              )}
              onClick={() => navigate('/settings/connectors')}
            >
              Uninstall connector
            </button>
          </GlassCard>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
